import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import AdSlot from "@/components/AdSlot";
import Breadcrumb from "@/components/Breadcrumb";
import SEO from "@/components/SEO";

export default function CategoryPage() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "not_found"

  useEffect(() => {
    let cancelled = false;
    setCat(null);
    setItems([]);
    setStatus("loading");
    (async () => {
      try {
        const c = await api.category(slug);
        if (cancelled) return;
        setCat(c);
        setStatus("ok");
        const list = await api.articles({ category: slug, limit: 50 });
        if (!cancelled) setItems(list);
      } catch {
        if (!cancelled) setStatus("not_found");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (status === "loading") {
    return (
      <div data-testid="category-loading" className="max-w-7xl mx-auto px-6 py-24 text-center text-foreground/60">
        Loading…
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <>
        <SEO title="Category not found" url={`/category/${slug}`} />
        <div data-testid="category-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="overline">404</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-3">
            This desk doesn't exist.
          </h1>
          <p className="mt-4 text-foreground/70">
            The category "{slug}" is not one of our six editorial desks. It may have been renamed or the link is a typo.
          </p>
          <Link
            to="/"
            data-testid="category-not-found-home"
            className="inline-block mt-8 px-6 py-3 bg-foreground text-background text-sm font-semibold hover:bg-primary transition-colors duration-200"
          >
            Return home
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={cat.name}
        description={cat.description}
        keywords={`${cat.name}, finance, ${cat.slug}, investing, finance topia`}
        url={`/category/${cat.slug}`}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb
          items={[{ label: "Home", to: "/" }, { label: cat.name }]}
        />
      </div>

      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b-2 border-foreground pb-8">
        <div className="overline text-primary">Desk</div>
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tight mt-2">
          {cat.name}<span className="italic font-normal">.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-foreground/70 leading-relaxed">
          {cat.description}
        </p>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {items.length === 0 ? (
          <div data-testid="category-empty" className="py-24 text-center text-foreground/60">
            No articles yet in this desk.
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((a) => (
              <ArticleCard key={a.id} a={a} />
            ))}
          </div>
        )}
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdSlot size="leaderboard" />
      </div>
    </>
  );
}
